import { useCallback, useEffect, useRef, useState } from 'react'
import {
  getLiveColumns,
  getLiveTables,
  getLiveTableSample,
  type LiveColumnInfo,
  type LiveTableEntry,
  type SyncTarget,
} from '@/api/designers'

/**
 * The project's LIVE database catalog — what is actually deployed, as opposed
 * to `getTables()` which lists the design.
 *
 * Owned by one hook because three consumers need the same answer and must not
 * each fetch it: the left rail (grouping project tables vs DB-only ones), the
 * Data tab (is this table in the project?), and the SQL console (autocomplete
 * schema + draggable column lists).
 *
 * Column lists are fetched lazily per table (`limit=0` → structure only) and
 * cached for the life of the hook, so expanding a table in the rail costs one
 * request the first time and nothing afterwards.
 */
export interface LiveCatalog {
  tables: LiveTableEntry[]
  /** Designed but never deployed — surfaced so the gap is visible both ways. */
  designOnly: string[]
  schema: string
  dialect: string
  loading: boolean
  /** Set when the catalog could not be read (bad connection, no preview DB…). */
  error: string | null
  /**
   * Bumped whenever the column cache changes. `columnsFor` is a stable
   * callback reading a ref, so it can't serve as a memo dependency — without
   * this counter the SQL console's autocomplete would keep the schema it
   * built on first render and never see columns loaded afterwards.
   */
  columnsVersion: number
  refresh: (counts?: 'estimate' | 'exact' | 'none') => Promise<void>
  /**
   * DETAILED columns for a table (type, nullability, PK) — what the rail
   * renders. Undefined until `loadColumns` resolves. Kept distinct from the
   * bulk map below, which is names and types only.
   */
  columnsFor: (table: string) => LiveColumnInfo[] | undefined
  loadColumns: (table: string) => Promise<LiveColumnInfo[]>
  /**
   * `{table: [column names]}` for the WHOLE schema — the console's
   * autocomplete source. Populated by `loadAllColumns` in one request;
   * a per-table fetch would be hundreds of round trips, and an autocomplete
   * that only knows the tables you already clicked is not worth having.
   */
  columnNames: Record<string, string[]>
  loadAllColumns: () => Promise<void>
  /** Drop one table's cached columns (after an adopt / structure change). */
  invalidateColumns: (table: string) => void
}

export function useLiveCatalog(project: string, target: SyncTarget): LiveCatalog {
  const [tables, setTables] = useState<LiveTableEntry[]>([])
  const [designOnly, setDesignOnly] = useState<string[]>([])
  const [schema, setSchema] = useState('')
  const [dialect, setDialect] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Columns are cached in a ref (identity-stable for callbacks) and mirrored
  // into state only to trigger re-renders when a new table's columns arrive.
  const columnCache = useRef<Record<string, LiveColumnInfo[]>>({})
  const [columnsVersion, setColumnTick] = useState(0)
  const inFlight = useRef<Record<string, Promise<LiveColumnInfo[]>>>({})

  const refresh = useCallback(async (counts: 'estimate' | 'exact' | 'none' = 'estimate') => {
    setLoading(true)
    try {
      const res = await getLiveTables(project, target, counts)
      if (res.success) {
        setTables(res.tables || [])
        setDesignOnly(res.design_only || [])
        setSchema(res.schema || '')
        setDialect(res.dialect || '')
        setError(null)
      } else {
        setTables([])
        setDesignOnly([])
        setError(res.message || res.error || 'Could not read the live database.')
      }
    } catch (e) {
      setTables([])
      setDesignOnly([])
      setError(e instanceof Error ? e.message : 'Could not read the live database.')
    } finally {
      setLoading(false)
    }
  }, [project, target])

  // Switching target (preview ↔ deployment) is a different database entirely,
  // so cached columns from the previous one must not survive it.
  useEffect(() => {
    columnCache.current = {}
    inFlight.current = {}
    setColumnTick((n) => n + 1)
    void refresh()
  }, [refresh])

  const columnsFor = useCallback(
    (table: string) => columnCache.current[table.toLowerCase()],
    [],
  )

  const loadColumns = useCallback(async (table: string): Promise<LiveColumnInfo[]> => {
    const key = table.toLowerCase()
    const cached = columnCache.current[key]
    if (cached) return cached
    // Dedupe concurrent requests for the same table (rail expand + console
    // autocomplete can both ask at once).
    const pending = inFlight.current[key]
    if (pending) return pending

    const promise = (async () => {
      try {
        const res = await getLiveTableSample(project, table, target, { limit: 0 })
        const cols = res.success ? (res.columns || []) : []
        columnCache.current[key] = cols
        setColumnTick((n) => n + 1)
        return cols
      } catch {
        columnCache.current[key] = []
        setColumnTick((n) => n + 1)
        return []
      } finally {
        delete inFlight.current[key]
      }
    })()
    inFlight.current[key] = promise
    return promise
  }, [project, target])

  // One catalog query for the whole schema, kept SEPARATE from columnCache.
  // The bulk query has no pk/nullable/default, so folding it into the
  // detailed cache would silently strip the key icons off the rail.
  const [columnNames, setColumnNames] = useState<Record<string, string[]>>({})
  const allLoaded = useRef(false)
  const loadAllColumns = useCallback(async () => {
    if (allLoaded.current) return
    allLoaded.current = true
    try {
      const res = await getLiveColumns(project, target)
      if (!res.success || !res.tables) {
        allLoaded.current = false
        return
      }
      const map: Record<string, string[]> = {}
      for (const [table, cols] of Object.entries(res.tables)) {
        map[table] = cols.map((c) => c.column)
      }
      setColumnNames(map)
    } catch {
      // Autocomplete degrades to table names only — never a hard failure.
      allLoaded.current = false
    }
  }, [project, target])

  useEffect(() => {
    allLoaded.current = false
    setColumnNames({})
  }, [project, target])

  const invalidateColumns = useCallback((table: string) => {
    delete columnCache.current[table.toLowerCase()]
    setColumnTick((n) => n + 1)
  }, [])

  return {
    tables, designOnly, schema, dialect, loading, error, columnsVersion,
    refresh, columnsFor, loadColumns,
    columnNames, loadAllColumns, invalidateColumns,
  }
}
